import { createFileRoute, notFound } from "@tanstack/react-router";
import {
  Compass, MessageCircle, Users, Video, Palette, BookOpen, Calendar,
  Sparkles, TrendingUp, Camera, Film, PenTool, Layers, Heart, ShieldCheck,
  Target, BarChart3, Mic, Megaphone, Eye,
} from "lucide-react";
import { ServicePage, type ServicePageData } from "@/components/sections/service-page";

const CHILDREN: Record<string, ServicePageData & { metaDesc: string }> = {
  "social-strategy": {
    eyebrow: "Social & Content · Social",
    title: "Social Strategy",
    sub: "A focused playbook for what to post, where, and why.",
    metaDesc: "Social media strategy: audience research, channel selection, content pillars, calendars and measurement.",
    intro:
      "We stop the random posting. With audience research, channel scoring and content pillars tied to business outcomes, you get a playbook your team (or ours) can execute against for the next 12 months.",
    features: [
      { icon: Compass, title: "Audience research", description: "Where your buyers actually spend time and what they engage with." },
      { icon: Target, title: "Channel selection", description: "1–3 platforms picked for ROI, not vanity reach." },
      { icon: Layers, title: "Content pillars", description: "3–5 pillars that ladder up to brand and business goals." },
      { icon: Calendar, title: "Editorial calendar", description: "12-month themes, launches and seasonal hooks." },
      { icon: BarChart3, title: "Measurement plan", description: "KPIs, dashboards and quarterly review cadence." },
      { icon: Megaphone, title: "Voice & tone", description: "Voice doc and tone guardrails for consistency." },
    ],
    process: [
      { step: "01", title: "Audit", description: "Current performance and competitor scan." },
      { step: "02", title: "Research", description: "Customer + audience interviews and listening." },
      { step: "03", title: "Strategy", description: "Pillars, calendar, voice and measurement." },
      { step: "04", title: "Handoff", description: "Workshop with your team or our production team." },
    ],
    deliverables: ["Audit report", "Audience + persona research", "Content pillars + voice doc", "12-month editorial calendar", "Measurement plan", "Strategy workshop"],
    techStack: ["Sprout Social", "Sparktoro", "Brand24", "Notion", "Looker Studio"],
    pricing: [
      { name: "Lite", price: "$3k", features: ["1 platform", "Pillars + 3-month calendar", "Workshop"] },
      { name: "Full strategy", price: "$8k", period: "project", highlighted: true, features: ["Up to 3 platforms", "Full research", "12-month calendar"] },
      { name: "Embedded", price: "$4k", period: "month", features: ["Strategy stewardship", "Monthly review", "Quarterly updates"] },
    ],
    faqs: [
      { q: "Will you execute too?", a: "Yes — strategy hands cleanly into our content production and community management retainers." },
      { q: "B2B and B2C?", a: "Both, with platform and pillar mixes tuned to each." },
    ],
  },
  "community-management": {
    eyebrow: "Social & Content · Social",
    title: "Community Management",
    sub: "Reply, DM and nurture — in your voice, with proper guardrails.",
    metaDesc: "Community management: comments, DMs, escalation playbooks, sentiment monitoring and reporting.",
    intro:
      "Community managers who actually understand your brand — replying within hours, defusing the noisy, surfacing the gold, and routing leads to sales. Backed by a written playbook and sentiment monitoring.",
    features: [
      { icon: MessageCircle, title: "Reply + DM coverage", description: "Mon–Sat coverage windows (24/7 by arrangement)." },
      { icon: ShieldCheck, title: "Response playbook", description: "Approved language, escalation paths and red-flag triggers." },
      { icon: Heart, title: "Engagement campaigns", description: "Outbound engagement on creators, partners and customers." },
      { icon: Eye, title: "Sentiment monitoring", description: "Brand listening across channels with weekly digest." },
      { icon: Users, title: "Lead routing", description: "Qualified DMs handed to sales via your CRM." },
      { icon: BarChart3, title: "Reporting", description: "Response times, sentiment trends and surfaced insights." },
    ],
    process: [
      { step: "01", title: "Onboard", description: "Voice, playbook, escalation paths." },
      { step: "02", title: "Train", description: "Brand immersion and red-flag training." },
      { step: "03", title: "Run", description: "Daily coverage and weekly review." },
      { step: "04", title: "Refine", description: "Playbook updates from real signal." },
    ],
    deliverables: ["Response playbook", "Escalation matrix", "Daily coverage", "Weekly insight digest", "Sentiment dashboard", "Monthly performance review"],
    techStack: ["Sprout Social", "Khoros", "Brand24", "Slack", "HubSpot"],
    pricing: [
      { name: "Single channel", price: "$1.5k", period: "month", features: ["1 platform", "Mon–Fri coverage", "Monthly report"] },
      { name: "Multi-channel", price: "$3.5k", period: "month", highlighted: true, features: ["Up to 4 platforms", "Mon–Sat coverage", "Sentiment dashboard"] },
      { name: "24/7", price: "$8k", period: "month", features: ["All channels", "Around-the-clock", "Lead routing + CRM"] },
    ],
    faqs: [
      { q: "Can you handle crises?", a: "Yes — escalation paths, holding statements and a comms lead on retainers above $3.5k." },
      { q: "Will you sound like us?", a: "Voice doc + weekly QA + final-say from your brand lead keep the tone tight." },
    ],
  },
  "influencer-marketing": {
    eyebrow: "Social & Content · Social",
    title: "Influencer Marketing",
    sub: "Sourced, briefed, contracted and measured — not vibes.",
    metaDesc: "Influencer and creator marketing: sourcing, briefing, contracts, whitelisting and performance reporting.",
    intro:
      "We run influencer programs that actually move metrics — vetted creators, performance briefs, usage rights, Spark/Allowlisting and proper attribution — not one-off gifting and hope.",
    features: [
      { icon: Users, title: "Creator sourcing", description: "Vetted roster across niches with audience-fit scoring." },
      { icon: PenTool, title: "Briefs + scripts", description: "Performance briefs with hooks, CTAs and dos/don'ts." },
      { icon: ShieldCheck, title: "Contracts + rights", description: "Usage, exclusivity and disclosure built into every deal." },
      { icon: Sparkles, title: "Whitelisting + Spark", description: "Run creator content as paid ads from their handle." },
      { icon: TrendingUp, title: "Performance briefs", description: "CTR/ROAS targets and per-creator scorecards." },
      { icon: BarChart3, title: "Attribution", description: "UTMs, promo codes and lift studies." },
    ],
    process: [
      { step: "01", title: "Plan", description: "Goals, ICP and creator archetypes." },
      { step: "02", title: "Source", description: "Vet, outreach and contracts." },
      { step: "03", title: "Run", description: "Brief, produce, review and ship." },
      { step: "04", title: "Measure", description: "Performance review and roster refresh." },
    ],
    deliverables: ["Creator roster + scorecards", "Briefs + scripts", "Signed contracts", "Spark Ads + whitelisting", "Performance reports", "Quarterly roster refresh"],
    techStack: ["Aspire", "Grin", "Tagger", "Triple Whale", "Spark Ads"],
    pricing: [
      { name: "Pilot", price: "$5k", features: ["5 creators", "1 campaign", "Performance report"] },
      { name: "Always-on", price: "$8k", period: "month", highlighted: true, features: ["8–15 creators / month", "Spark Ads", "Monthly review"] },
      { name: "Scale", price: "$20k", period: "month", features: ["30+ creators / month", "Dedicated lead", "Lift studies"] },
    ],
    faqs: [
      { q: "Do you cover creator fees?", a: "Creator fees and ad spend are separate from our retainer." },
      { q: "Micro or macro?", a: "Mostly micro/mid — they out-perform on ROAS and look more native in Spark." },
    ],
  },
  "video-production": {
    eyebrow: "Social & Content · Creative",
    title: "Video Production",
    sub: "Short-form, brand films and product videos shot, edited and ready to ship.",
    metaDesc: "Video production: short-form social, brand films, product videos, UGC and motion graphics — pre-production to delivery.",
    intro:
      "From TikToks and Reels to brand films and product launches — we handle pre-production, shoot, edit and motion. In-studio, on-location or remote with shoot kits for founders.",
    features: [
      { icon: Video, title: "Short-form video", description: "Reels, Shorts, TikToks edited for native algorithms." },
      { icon: Camera, title: "Studio + on-location", description: "Pro shoots with lighting, sound and direction." },
      { icon: Film, title: "Brand films", description: "Anthem, manifesto and case-study films." },
      { icon: Sparkles, title: "Motion graphics", description: "Type-driven and product motion in After Effects." },
      { icon: Mic, title: "Podcast video", description: "Multi-cam podcast capture and editorial." },
      { icon: ShieldCheck, title: "Usage rights", description: "Full commercial rights for paid + organic." },
    ],
    process: [
      { step: "01", title: "Brief", description: "Goals, formats, hooks and shot list." },
      { step: "02", title: "Shoot", description: "On-location or studio with full crew." },
      { step: "03", title: "Edit", description: "Native cuts per platform with captions." },
      { step: "04", title: "Deliver", description: "Final assets + usage rights + variations." },
    ],
    deliverables: ["Shot list + storyboard", "Raw + edited footage", "Platform-native cuts", "Captions + subtitles", "Motion graphics", "Usage rights doc"],
    techStack: ["Premiere Pro", "After Effects", "DaVinci Resolve", "CapCut", "Frame.io"],
    pricing: [
      { name: "UGC pack", price: "$2.5k", features: ["6 short-form edits", "From your footage"] },
      { name: "Shoot day", price: "$8k", highlighted: true, features: ["Full crew + 1 shoot day", "20+ edited cuts", "Motion graphics"] },
      { name: "Brand film", price: "$25k", period: "project", features: ["2-day shoot", "Hero film + cutdowns", "Music + sound design"] },
    ],
    faqs: [
      { q: "Can you film founders?", a: "Yes — we ship remote shoot kits with direction over Zoom." },
      { q: "Do you license music?", a: "Yes — properly licensed via Musicbed, Artlist or custom composition." },
    ],
  },
  "graphic-design": {
    eyebrow: "Social & Content · Creative",
    title: "Graphic Design",
    sub: "Social graphics, ad creative and brand visuals at production speed.",
    metaDesc: "Graphic design for social, ads, landing pages and brand systems — production-ready creative at scale.",
    intro:
      "On-brand, conversion-aware graphics shipped on cadence — social posts, ad creative, presentations, landing-page art, and the systems behind them so quality stays high as volume scales.",
    features: [
      { icon: Palette, title: "Social graphics", description: "Carousel, single-image and story templates per platform." },
      { icon: Sparkles, title: "Ad creative", description: "Static and motion ads tuned for hook, hold and CTA." },
      { icon: Layers, title: "Brand systems", description: "Templates and component libraries for in-house speed." },
      { icon: BookOpen, title: "Presentations", description: "Sales decks, pitch decks and investor updates." },
      { icon: PenTool, title: "Landing-page art", description: "Heroes, illustrations and section art for landers." },
      { icon: TrendingUp, title: "Production cadence", description: "Weekly drops with batching and review pipelines." },
    ],
    process: [
      { step: "01", title: "Brand audit", description: "Tokens, type, color and pattern review." },
      { step: "02", title: "System", description: "Templates and components for repeat work." },
      { step: "03", title: "Production", description: "Weekly drops with batched review." },
      { step: "04", title: "Refresh", description: "Quarterly template + creative refresh." },
    ],
    deliverables: ["Template library", "Weekly creative drops", "Source files + brand kit", "Ad creative", "Pitch decks", "Quarterly refresh"],
    techStack: ["Figma", "Adobe Creative Suite", "After Effects", "Canva", "Frame.io"],
    pricing: [
      { name: "Lite", price: "$1.5k", period: "month", features: ["15 graphics / month", "1 round of revisions"] },
      { name: "Growth", price: "$3.5k", period: "month", highlighted: true, features: ["40 graphics + 5 motion", "2 rounds revisions", "Template library"] },
      { name: "Scale", price: "$8k", period: "month", features: ["Unlimited (fair use)", "Dedicated designer", "Brand stewardship"] },
    ],
    faqs: [
      { q: "Do you do illustration?", a: "Yes — light and editorial illustration is part of Growth and Scale plans." },
      { q: "Source files included?", a: "Always — Figma, Illustrator and AE files are yours from day one." },
    ],
  },
  "brand-storytelling": {
    eyebrow: "Social & Content · Creative",
    title: "Brand Storytelling",
    sub: "Narrative, messaging and the long-form story your brand keeps telling.",
    metaDesc: "Brand storytelling: messaging architecture, narrative framework, founder voice and long-form content programs.",
    intro:
      "Your brand isn't your logo — it's the story you keep telling. We build the messaging architecture and narrative system, then turn it into founder essays, manifestos, customer stories and long-form content that earns attention nothing else can.",
    features: [
      { icon: BookOpen, title: "Messaging architecture", description: "Core narrative, value props and proof points." },
      { icon: Mic, title: "Founder voice", description: "Ghost-write essays and posts in the founder's voice." },
      { icon: PenTool, title: "Customer stories", description: "Long-form case studies with quotes, photos and data." },
      { icon: Megaphone, title: "Manifestos + POV pieces", description: "Strong opinions on the questions your market is asking." },
      { icon: Camera, title: "Editorial photography", description: "Real, art-directed imagery beats stock every time." },
      { icon: TrendingUp, title: "Distribution", description: "Newsletter, LinkedIn, podcasts and syndication." },
    ],
    process: [
      { step: "01", title: "Discovery", description: "Founder + customer interviews and archive review." },
      { step: "02", title: "Architecture", description: "Narrative, pillars and proof points." },
      { step: "03", title: "Produce", description: "Essays, stories and editorial pieces." },
      { step: "04", title: "Distribute", description: "Owned + earned + paid amplification." },
    ],
    deliverables: ["Messaging architecture doc", "Founder voice playbook", "Long-form pieces (2–6 / month)", "Customer stories", "Editorial photography", "Distribution plan"],
    techStack: ["Notion", "Substack", "Ghost", "LinkedIn", "Beehiiv"],
    pricing: [
      { name: "Architecture", price: "$6k", features: ["Narrative + messaging doc", "Founder voice playbook"] },
      { name: "Always-on", price: "$6k", period: "month", highlighted: true, features: ["2 essays / month", "1 customer story", "Editorial review"] },
      { name: "Full editorial", price: "$15k", period: "month", features: ["6+ pieces / month", "Photography + design", "Distribution"] },
    ],
    faqs: [
      { q: "Will it sound like the founder?", a: "Yes — deep interviews, archive mining and a feedback loop until the voice is unmistakable." },
      { q: "Where does this run?", a: "Owned (blog, newsletter), LinkedIn, podcasts and selective syndication." },
    ],
  },
};

export const Route = createFileRoute("/marketing/social-content/$child")({
  component: SocialContentChild,
  loader: ({ params }) => {
    const item = CHILDREN[params.child];
    if (!item) throw notFound();
    return { item };
  },
  head: ({ params }) => {
    const item = CHILDREN[params.child];
    if (!item) return {};
    return {
      meta: [
        { title: `${item.title} — CoderLity` },
        { name: "description", content: item.metaDesc },
        { property: "og:title", content: `${item.title} — CoderLity` },
        { property: "og:description", content: item.metaDesc },
        { property: "og:url", content: `/marketing/social-content/${params.child}` },
      ],
      links: [{ rel: "canonical", href: `https://coderlity.com/marketing/social-content/${params.child}` }],
    };
  },
});

function SocialContentChild() {
  const { item } = Route.useLoaderData();
  return <ServicePage data={item} />;
}
